import React, { useMemo, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { STATUSES } from "../../constants/statuses";
//Import Breadcrumb
import { Container } from "reactstrap";
import "react-toastify/dist/ReactToastify.css";

import {
  UpdateProperty,
  UpdatePropertyListed,
  FetchProperties,
  SearchProperties,
  ExportListings,
} from "../../apis/Property";
import { downloadListingsWorkbook } from "./exportListingsWorkbook";
import { Search, Upload } from "lucide-react";

import Paginate from "../../components/shared/paginate";
import { toast } from "react-toastify";
import { GenerateShortToken } from "../../apis/Auth";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/shared/alertDialog";

const getIsListed = (property) => Boolean(property?.listed);

const Property = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_LISTINGS);
  const [shortToken, setShortToken] = useState("");
  const [properties, setProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  // const [users, setUsers] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [exporting, setExporting] = useState(false);
  const [listedConfirm, setListedConfirm] = useState(null);
  const [updatingListed, setUpdatingListed] = useState(false);
  const limit = "10";

  // short token cache + helpers
  const tokenTimestampRef = useRef(0);
  const generatingPromiseRef = useRef(null);
  const TOKEN_TTL_MS = 3 * 60 * 1000; // 3 minutes

  useEffect(() => {
    const generateShortToken = async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
      } catch (err) {
        toast.error("Failed to generate short token");
      }
    };
    generateShortToken();
  }, []);

  // Return a valid token: reuse until TTL, otherwise generate a new one.
  const getValidShortToken = async () => {
    // If token exists and is not expired, return it
    if (
      shortToken &&
      tokenTimestampRef.current &&
      Date.now() - tokenTimestampRef.current < TOKEN_TTL_MS
    ) {
      return shortToken;
    }

    // If a generation is already in-flight, wait for it
    if (generatingPromiseRef.current) {
      try {
        await generatingPromiseRef.current;
        return shortToken;
      } catch {
        return "";
      }
    }

    // Otherwise, create a generation promise and await it
    generatingPromiseRef.current = (async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
        return token;
      } catch (err) {
        toast.error("Failed to generate short token");
        return "";
      } finally {
        generatingPromiseRef.current = null;
      }
    })();

    const newToken = await generatingPromiseRef.current;
    return newToken || "";
  };

  const fetchProperties = async () => {
    try {
      const response = await FetchProperties(currentPage, limit); // Assuming FetchHouseTypes is the correct function from your API
      if (response.success) {
        setProperties(response?.data?.listings);
        setFilteredProperties(response?.data?.listings);
        setTotalPages(response?.data?.totalPages);
      } else {
        toast.error("Failed to fetch properties. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to fetch properties. Please try again later.");
    }
  };

  const exportListings = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await ExportListings();
      const columns = response?.data?.columns;
      const rows = response?.data?.rows;
      if (!response?.success || !Array.isArray(columns) || !Array.isArray(rows)) {
        toast.error("Failed to export listings.");
        return;
      }
      await downloadListingsWorkbook({ columns, rows });
      toast.success(
        rows.length
          ? `Exported ${rows.length} listing${rows.length === 1 ? "" : "s"}.`
          : "Export downloaded. No listings to include.",
      );
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to export listings.");
    } finally {
      setExporting(false);
    }
  };

  const searchProperties = async () => {
    try {
      const response = await SearchProperties(keyword, currentPage, limit);
      if (response.success) {
        setFilteredProperties(response?.data?.listings);
        setTotalPages(response?.data?.totalPages);
      } else {
        toast.error("Failed to search properties. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to search properties. Please try again later.");
    }
  };

  useEffect(() => {
    if (keyword) {
      searchProperties();
    } else {
      fetchProperties();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, keyword]);

  useEffect(() => {
    if (keyword) {
      setCurrentPage("1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  async function handleUpdateProperty(propertyId, status) {
    const res = await UpdateProperty({ propertyId, status });
    if (res?.success) {
      if (status === STATUSES.RECOMMENDED) {
        toast.success("Property recommended status updated");
        setFilteredProperties((prev) =>
          prev.map((property) =>
            property._id === propertyId
              ? { ...property, recommended: !property.recommended }
              : property,
          ),
        );
      } else {
        toast.success("Property delete status updated");
        setFilteredProperties((prev) =>
          prev.map((property) =>
            property._id === propertyId
              ? { ...property, deleted: !property.deleted }
              : property,
          ),
        );
      }
    }
  }

  const requestListedChange = (property, nextListed) => {
    if (getIsListed(property) === nextListed) return;
    setListedConfirm({
      propertyId: property._id,
      title: property.title || property._id,
      listed: nextListed,
    });
  };

  const confirmListedChange = async () => {
    if (!listedConfirm) return;
    const { propertyId, listed } = listedConfirm;
    setUpdatingListed(true);
    try {
      const res = await UpdatePropertyListed({ propertyId, listed });
      if (res?.success) {
        toast.success(
          listed
            ? "Property listed successfully"
            : "Property unlisted successfully",
        );
        setFilteredProperties((prev) =>
          prev.map((property) =>
            property._id === propertyId ? { ...property, listed } : property,
          ),
        );
        setListedConfirm(null);
      } else {
        toast.error(res?.message || "Failed to update listed status");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update listed status",
      );
    } finally {
      setUpdatingListed(false);
    }
  };

  const renderListedControl = (property) => {
    const isListed = getIsListed(property);
    if (!canManage) {
      return <span>{isListed ? "Yes" : "No"}</span>;
    }
    return (
      <select
        value={isListed ? "yes" : "no"}
        onChange={(e) =>
          requestListedChange(property, e.target.value === "yes")
        }
        className="rounded border border-gray-400 bg-transparent px-2 py-1 text-sm dark:border-white"
      >
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    );
  };

  const columns = useMemo(
    () => [
      "ID",
      "Total Units",
      "Title",
      "Host Name",
      "Host Email",
      "Address",
      "City",
      "State",
      "Country",
      "Created At",
      "Recommended",
      "Listed",
      "Edit",
      "View",
      "Delete",
    ],
    [],
  );

  const obscureEmail = (email) => {
    return email;
    // const [name, domain] = email?.split("@");
    // return `${name[0]}${new Array(name.length - 1).join("*")}${
    //   name[name.length - 1]
    // }@${domain[0]}${new Array(domain.length - 1).join("*")}${
    //   domain[domain.length - 1]
    // }`;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        {/* <div className="page-content">
        <Container>
          <div style={{ marginLeft: "200px" }}>
            <Breadcrumbs
              title="DATA TABLES"
              breadcrumbItems={breadcrumbItems}
            />
          </div>
          <Card style={{ marginLeft: "200px" }}>
            <CardBody>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              ></div>
              <TableContainer
                columns={columns || []}
                data={properties || []}
                isPagination={true}
                iscustomPageSize={false}
                isBordered={false}
                customPageSize={10}
                onRowClick={(id) => navigate("/property/" + id)}
                headerActionButton={
                  <Button
                    color="success"
                    className="text-nowrap"
                    onClick={() => DownloadAsCSV()}
                  >
                    Download As CSV
                  </Button>
                }
              />
            </CardBody>
          </Card>
        </Container>
      </div> */}
        <Container
          //  h-[calc(100vh-130px)]
          className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]"
          fluid
        >
          <div className="p-6 flex flex-col gap-6 ">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-wrap items-center gap-4">
                <h1 className="text-2xl dark:text-[#c7d8eb]">
                  Manage listings
                </h1>
                <Link
                  to="/property/in-progress"
                  className="text-secondaryColor font-semibold"
                >
                  In progress listings
                </Link>
              </div>
              {canManage && (
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/property/create"
                    className="rounded-md bg-secondaryColor px-4 py-2 text-white"
                  >
                    Create new listing for any user
                  </Link>
                  <Link
                    to="/property/bulk-create"
                    className="rounded-md border border-secondaryColor px-4 py-2 text-secondaryColor"
                  >
                    Bulk create
                  </Link>
                </div>
              )}
            </div>
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="relative border max-w-[400px] border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
                <div className="flex lg:justify-self-end">
                  <button
                    type="button"
                    onClick={exportListings}
                    disabled={exporting}
                    className="flex items-center gap-2 disabled:opacity-60"
                  >
                    {exporting ? "Exporting..." : "Export data into Excel"}
                    <div className="shrink-0 bg-white p-1">
                      <Upload className="" size={16} />
                    </div>
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto ">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 0 ? "w-[12%]" : "w-[11%]"
                      } min-w-[130px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>
                {properties &&
                  filteredProperties.map((property, index) => (
                    <>
                      <div
                        key={`property-desktop-${index}`}
                        className="hidden lg:flex"
                      >
                        <div className="w-[6.2%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property._id}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.totalUnits}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.title}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.host?.firstName}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {obscureEmail(property.host?.email)}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.location?.street &&
                          property.location?.city?.en &&
                          property.location?.state &&
                          property.location?.country &&
                          property.location?.zip
                            ? property.location?.street +
                              ", " +
                              property.location?.city?.en +
                              ", " +
                              property.location?.state +
                              ", " +
                              property.location?.country?.en +
                              ", " +
                              property.location?.zip
                            : ""}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.location?.city?.en}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.location?.state}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.location?.country?.en}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {property.createdAt?.toString()?.slice(0, 10)}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            property.recommended ? (
                              <button
                                onClick={() =>
                                  handleUpdateProperty(
                                    property._id,
                                    "recommended",
                                  )
                                }
                                className="text-secondaryColor font-semibold"
                              >
                                Remove
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  handleUpdateProperty(
                                    property._id,
                                    "recommended",
                                  )
                                }
                                className="text-secondaryColor font-semibold"
                              >
                                Set
                              </button>
                            )
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {renderListedControl(property)}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            <button
                              type="button"
                              className="text-secondaryColor"
                              onClick={async () => {
                                const token = await getValidShortToken();
                                if (token) {
                                  const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/listing/create?propertyId=${property._id}&token=${token}`;
                                  window.open(url, "_blank");
                                }
                              }}
                            >
                              Edit
                            </button>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <button
                            type="button"
                            className="text-secondaryColor"
                            onClick={async () => {
                              const token = await getValidShortToken();
                              if (token) {
                                const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${property._id}?token=${token}`;
                                window.open(url, "_blank");
                              }
                            }}
                          >
                            View
                          </button>
                        </div>
                        <div className="w-[6.7%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            property.deleted ? (
                              <button
                                onClick={() =>
                                  handleUpdateProperty(property._id, "deleted")
                                }
                                className="text-secondaryColor font-semibold"
                              >
                                Recover
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  handleUpdateProperty(property._id, "deleted")
                                }
                                className="text-secondaryColor font-semibold"
                              >
                                Delete
                              </button>
                            )
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </div>
                      <div
                        key={`property-mobile-${index}`}
                        className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white"
                      >
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">ID</div>
                          <div>{property._id}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Total Units</div>
                          <div>{property.totalUnits}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Title</div>
                          <div>{property.title}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Host Name</div>
                          <div>{property.host?.firstName}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Host Email</div>
                          <div>{obscureEmail(property.host?.email)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Address</div>
                          <div>
                            {property.location?.street &&
                            property.location?.city?.en &&
                            property.location?.state &&
                            property.location?.country &&
                            property.location?.zip
                              ? property.location?.street +
                                ", " +
                                property.location?.city?.en +
                                ", " +
                                property.location?.state +
                                ", " +
                                property.location?.country?.en +
                                ", " +
                                property.location?.zip
                              : ""}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">city</div>
                          <div>{property.location?.city?.en}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">State</div>
                          <div>{property.location?.state}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Country</div>
                          <div>{property.location?.country?.en}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Created At</div>
                          <div>
                            {property.createdAt?.toString()?.slice(0, 10)}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Recommended</div>
                          <div>
                            {canManage ? (
                              property.recommended ? (
                                <button
                                  onClick={() =>
                                    handleUpdateProperty(
                                      property._id,
                                      "recommended",
                                    )
                                  }
                                  className="text-secondaryColor font-semibold"
                                >
                                  Remove
                                </button>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleUpdateProperty(
                                      property._id,
                                      "recommended",
                                    )
                                  }
                                  className="text-secondaryColor font-semibold"
                                >
                                  Set
                                </button>
                              )
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Listed</div>
                          <div>{renderListedControl(property)}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Edit</div>
                          <div>
                            {canManage ? (
                              <button
                                type="button"
                                className="text-secondaryColor"
                                onClick={async () => {
                                  const token = await getValidShortToken();
                                  if (token) {
                                    const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/listing/create?propertyId=${property._id}&token=${token}`;
                                    window.open(url, "_blank");
                                  }
                                }}
                              >
                                Edit
                              </button>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">View</div>
                          <div>
                            <button
                              type="button"
                              className="text-secondaryColor"
                              onClick={async () => {
                                const token = await getValidShortToken();
                                if (token) {
                                  const url = `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${property._id}?token=${token}`;
                                  window.open(url, "_blank");
                                }
                              }}
                            >
                              View
                            </button>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Delete</div>
                          <div>
                            {canManage ? (
                              property.deleted ? (
                                <button
                                  onClick={() =>
                                    handleUpdateProperty(
                                      property._id,
                                      "deleted",
                                    )
                                  }
                                  className="text-secondaryColor font-semibold"
                                >
                                  Recover
                                </button>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleUpdateProperty(
                                      property._id,
                                      "deleted",
                                    )
                                  }
                                  className="text-secondaryColor font-semibold"
                                >
                                  Delete
                                </button>
                              )
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ))}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
        </Container>
      </div>

      <AlertDialog
        open={Boolean(listedConfirm)}
        onOpenChange={(open) => {
          if (!open && !updatingListed) setListedConfirm(null);
        }}
      >
        <AlertDialogContent className="w-[min(100%,22rem)] max-w-sm gap-3 bg-gray-200 p-4 text-gray-900 dark:bg-[#171d37] dark:text-[#c7d8eb]">
          <AlertDialogHeader className="space-y-1 text-left sm:text-left">
            <AlertDialogTitle className="text-base">
              Are you sure?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm">
              {listedConfirm
                ? `This will ${
                    listedConfirm.listed ? "list" : "unlist"
                  } "${listedConfirm.title}".`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-end gap-2">
            <AlertDialogCancel disabled={updatingListed}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmListedChange();
              }}
              disabled={updatingListed}
              className="bg-secondaryColor text-white"
            >
              {updatingListed ? "Updating..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </React.Fragment>
  );
};

export default Property;
